import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import type { RealtimeEvent } from '@/types/email';
import { useTodoStore } from './todoStore';
import { useOperationStore } from './operationStore';
import type { TodoList, TodoProject, TodoTag } from '@/types/todo';
import { toast } from 'sonner';

const MAX_QUEUE_SIZE = 150;

type TodoEvent = Extract<
  RealtimeEvent,
  | { type: 'todo.task.created' | 'todo.task.updated' | 'todo.task.deleted' }
  | { type: 'todo.list.created' | 'todo.list.updated' | 'todo.list.deleted' }
  | { type: 'todo.project.created' | 'todo.project.updated' | 'todo.project.deleted' }
  | { type: 'todo.tag.created' | 'todo.tag.updated' | 'todo.tag.deleted' }
>;

interface TodoRealtimeStoreState {
  queue: TodoEvent[];
  processing: boolean;
  documentVisible: boolean;
  enqueueEvent: (event: RealtimeEvent) => void;
  processQueue: () => Promise<void>;
  setDocumentVisibility: (visible: boolean) => void;
  clearQueue: () => void;
}

const isTodoEvent = (event: RealtimeEvent): event is TodoEvent => event.type.startsWith('todo.');

const getTodoEventDedupKey = (event: TodoEvent): string | null => {
  switch (event.type) {
    case 'todo.task.created':
    case 'todo.task.updated':
    case 'todo.task.deleted':
      return event.task?.id ? `${event.type}:${event.task.id}` : null;
    case 'todo.list.created':
    case 'todo.list.updated':
    case 'todo.list.deleted':
      return event.list && 'id' in event.list && event.list.id ? `${event.type}:${event.list.id}` : null;
    case 'todo.project.created':
    case 'todo.project.updated':
    case 'todo.project.deleted':
      return event.project && 'id' in event.project && event.project.id ? `${event.type}:${event.project.id}` : null;
    case 'todo.tag.created':
    case 'todo.tag.updated':
    case 'todo.tag.deleted':
      return event.tag && 'id' in event.tag && event.tag.id ? `${event.type}:${event.tag.id}` : null;
    default:
      return null;
  }
};

const processTodoRealtimeEvent = (event: TodoEvent) => {
  const todoStore = useTodoStore.getState();
  const operationStore = useOperationStore.getState();
  switch (event.type) {
    case 'todo.task.created':
    case 'todo.task.updated':
      if (event.task) {
        // Don't overwrite optimistic updates for tasks with pending operations
        const operationState = operationStore.getOperationState(event.task.id);
        if (operationState === 'pending') {
          // Skip this event - the optimistic update is in progress
          return;
        }
        // Don't overwrite with stale data - skip when we already have a more recent update
        const existingTask = todoStore.tasks[event.task.id];
        if (
          existingTask &&
          existingTask.updatedAt &&
          event.task.updatedAt &&
          existingTask.updatedAt.localeCompare(event.task.updatedAt) > 0
        ) {
          return;
        }

        // Show toast for ClickUp-created tasks
        if (event.type === 'todo.task.created' && !existingTask) {
          const isClickUpTask = event.task.integrations?.some(
            (integration: any) => integration.integrationType === 'clickup'
          );
          if (isClickUpTask) {
            toast.success('New task added from ClickUp', {
              description: event.task.title,
              duration: 4000,
            });
          }
        }

        todoStore.upsertTask(event.task);
      }
      break;
    case 'todo.task.deleted':
      if (event.task?.id) {
        // Don't overwrite optimistic deletes for tasks with pending operations
        const operationState = operationStore.getOperationState(event.task.id);
        if (operationState === 'pending') {
          // Skip this event - the optimistic delete is in progress
          return;
        }
        // Don't process delete if we have an equally or more recent version of the task
        const existingTask = todoStore.tasks[event.task.id];
        if (
          existingTask &&
          existingTask.updatedAt &&
          event.task.updatedAt &&
          existingTask.updatedAt.localeCompare(event.task.updatedAt) >= 0
        ) {
          return;
        }
        todoStore.removeTask(event.task.id);
      }
      break;
    case 'todo.list.created':
    case 'todo.list.updated': {
      const list = event.list;
      if (list && 'name' in list) {
        todoStore.upsertList(list as TodoList);
      }
      break;
    }
    case 'todo.list.deleted':
      if (event.list && 'id' in event.list && event.list.id) {
        todoStore.removeList(event.list.id);
      }
      break;
    case 'todo.project.created':
    case 'todo.project.updated': {
      const project = event.project;
      if (project && 'name' in project) {
        todoStore.upsertProject(project as TodoProject);
      }
      break;
    }
    case 'todo.project.deleted':
      if (event.project && 'id' in event.project && event.project.id) {
        todoStore.removeProject(event.project.id);
      }
      break;
    case 'todo.tag.created':
    case 'todo.tag.updated': {
      const tag = event.tag;
      if (tag && 'name' in tag) {
        todoStore.upsertTag(tag as TodoTag);
      }
      break;
    }
    case 'todo.tag.deleted':
      if (event.tag && 'id' in event.tag && event.tag.id) {
        todoStore.removeTag(event.tag.id);
      }
      break;
  }
};

export const useTodoRealtimeStore = create<TodoRealtimeStoreState>()(
  conditionalDevtools(
    (set, get) => ({
      queue: [],
      processing: false,
      documentVisible: true,

      enqueueEvent: (event) => {
        if (!isTodoEvent(event)) {
          return;
        }
        set((state) => {
          const nextQueue = [...state.queue];
          const dedupKey = getTodoEventDedupKey(event);
          if (dedupKey) {
            for (let index = nextQueue.length - 1; index >= 0; index -= 1) {
              const key = getTodoEventDedupKey(nextQueue[index]);
              if (key === dedupKey) {
                nextQueue.splice(index, 1);
                break;
              }
            }
          }
          nextQueue.push(event);
          if (nextQueue.length > MAX_QUEUE_SIZE) {
            nextQueue.splice(0, nextQueue.length - MAX_QUEUE_SIZE);
          }
          return { queue: nextQueue };
        });

        const { processing, documentVisible } = get();
        if (documentVisible && !processing) {
          void get().processQueue();
        }
      },

      processQueue: async () => {
        if (get().processing) {
          return;
        }
        set({ processing: true });
        try {
          while (get().documentVisible) {
            const [event, ...rest] = get().queue;
            if (!event) {
              break;
            }
            set({ queue: rest });
            try {
              await Promise.resolve(processTodoRealtimeEvent(event));
            } catch (error) {
              console.error('Failed to process todo realtime event', error);
            }
          }
        } finally {
          set({ processing: false });
        }
      },

      setDocumentVisibility: (visible) => {
        set({ documentVisible: visible });
        if (visible && get().queue.length && !get().processing) {
          void get().processQueue();
        }
      },

      clearQueue: () => set({ queue: [] }),
    }),
    { name: 'TodoRealtimeStore' }
  )
);
